export default class CampFilters {
    constructor($form) {
        this.$form = $form;
        this.$block = $form.closest('.camp-list-block');
        this.$cards = Array.from(this.$block.querySelectorAll('[data-camp-card]'));
        this.$empty = this.$block.querySelector('[data-camp-filter-empty]');
        this.$location = $form.querySelector('[data-camp-filter-location]');
        this.$type = $form.querySelector('[data-camp-filter-type]');
        this.$period = $form.querySelector('[data-camp-filter-period]');
        this.fields = {
            location: this.$location,
            type: this.$type,
            period: this.$period,
        };
        this.optionCatalog = {
            location: this.captureOptions(this.$location),
            type: this.captureOptions(this.$type),
            period: this.captureOptions(this.$period),
        };
        this.cardPeriodDetails = new Map(
            this.$cards.map(($card) => [$card, this.captureCardPeriods($card)]),
        );
        this.cardMetaCatalog = new Map(
            this.$cards.map(($card) => [$card, this.captureCardMetaCatalog($card)]),
        );

        this.apply = this.apply.bind(this);
        this.reset = this.reset.bind(this);

        this.$form.addEventListener('input', this.apply);
        this.$form.addEventListener('change', this.apply);
        this.$form.addEventListener('reset', this.reset);
        this.$form.addEventListener('submit', (event) => {
            event.preventDefault();
            this.apply();
        });

        this.apply();
    }

    apply() {
        const filters = this.syncOptions();
        const location = filters.location;
        const type = filters.type;
        const period = filters.period;
        let visible = 0;

        this.$cards.forEach(($card) => {
            const matchesType = !type || $card.dataset.campType === type;
            const matchesLocation = !location || this.values($card.dataset.campLocations).includes(location);
            const matchesPeriod = !period || this.values($card.dataset.campPeriods).includes(period);
            const isVisible = matchesType && matchesLocation && matchesPeriod;

            $card.hidden = !isVisible;
            if (isVisible) {
                this.syncCardMeta($card, filters);
                visible++;
                return;
            }

            this.syncCardMeta($card, this.currentFilters());
        });

        if (this.$empty) {
            this.$empty.hidden = visible > 0;
        }
    }

    reset() {
        window.requestAnimationFrame(this.apply);
    }

    syncOptions() {
        const filters = this.currentFilters();

        ['location', 'type', 'period'].forEach((field) => {
            this.renderOptions(field, this.availableValues(field, filters), filters[field]);
        });

        return this.currentFilters();
    }

    currentFilters() {
        return {
            location: this.normalize(this.$location ? this.$location.value : ''),
            type: this.normalize(this.$type ? this.$type.value : ''),
            period: this.normalize(this.$period ? this.$period.value : ''),
        };
    }

    availableValues(field, filters) {
        const values = new Set();

        this.$cards.forEach(($card) => {
            if (!this.matchesOtherFilters($card, field, filters)) {
                return;
            }

            if (field === 'location') {
                this.values($card.dataset.campLocations).forEach((value) => values.add(value));
                return;
            }

            if (field === 'type') {
                const value = this.normalize($card.dataset.campType || '');
                if (value) {
                    values.add(value);
                }
                return;
            }

            this.values($card.dataset.campPeriods).forEach((value) => values.add(value));
        });

        return values;
    }

    matchesOtherFilters($card, ignoredField, filters) {
        const matchesType = ignoredField === 'type' || !filters.type || this.normalize($card.dataset.campType || '') === filters.type;
        const matchesLocation = ignoredField === 'location' || !filters.location || this.values($card.dataset.campLocations).includes(filters.location);
        const matchesPeriod = ignoredField === 'period' || !filters.period || this.values($card.dataset.campPeriods).includes(filters.period);

        return matchesType && matchesLocation && matchesPeriod;
    }

    renderOptions(field, allowedValues, selectedValue) {
        const $select = this.fields[field];
        if (!$select) {
            return;
        }

        const { placeholder, options } = this.optionCatalog[field];
        const visibleOptions = options.filter((option) => allowedValues.has(option.normalizedValue));
        const hasSelection = selectedValue && visibleOptions.some((option) => option.normalizedValue === selectedValue);

        $select.innerHTML = '';
        $select.appendChild(new Option(placeholder.label, placeholder.value));

        visibleOptions.forEach((option) => {
            $select.appendChild(new Option(option.label, option.value));
        });

        $select.value = hasSelection ? visibleOptions.find((option) => option.normalizedValue === selectedValue).value : placeholder.value;
    }

    captureOptions($select) {
        if (!$select) {
            return {
                placeholder: { value: '', label: '' },
                options: [],
            };
        }

        const options = Array.from($select.options).map(($option, index) => ({
            value: $option.value,
            label: $option.textContent.trim(),
            normalizedValue: this.normalize($option.value),
            index,
        }));

        const [placeholder, ...rest] = options;

        return {
            placeholder: placeholder || { value: '', label: '' },
            options: rest,
        };
    }

    captureCardPeriods($card) {
        try {
            const details = JSON.parse($card.dataset.campPeriodDetails || '[]');

            if (!Array.isArray(details)) {
                return [];
            }

            return details.map((detail) => ({
                vacation: detail.vacation || '',
                location: detail.location || '',
                date: detail.date || '',
                periodValue: this.normalize(detail.periodValue || detail.vacation || ''),
                locationValue: this.normalize(detail.locationValue || detail.location || ''),
            }));
        } catch (error) {
            return [];
        }
    }

    captureCardMetaCatalog($card) {
        const periods = this.cardPeriodDetails.get($card) || [];

        return {
            vacations: this.uniqueByNormalized(periods.map((period) => ({
                label: period.vacation,
                normalized: period.periodValue,
            }))),
            locations: this.uniqueByNormalized(periods.map((period) => ({
                label: period.location,
                normalized: period.locationValue,
            }))),
        };
    }

    syncCardMeta($card, filters) {
        const detail = this.resolveCardPeriod($card, filters);
        if (!detail) {
            return;
        }

        this.setMetaText($card, '[data-camp-meta-vacation]', detail.vacation);
        this.setMetaText($card, '[data-camp-meta-location]', detail.location);
        this.setMetaDates($card, filters);
        this.setMetaExtra(
            $card,
            '[data-camp-meta-vacation-extra]',
            this.buildMetaExtra($card, 'vacations', detail.periodValue, 'Ook in'),
        );
        this.setMetaExtra(
            $card,
            '[data-camp-meta-location-extra]',
            this.buildMetaExtra($card, 'locations', detail.locationValue, 'Ook in'),
        );
    }

    resolveCardPeriod($card, filters) {
        const periods = this.resolveCardPeriods($card, filters);
        if (!periods.length) {
            return null;
        }

        return periods[0];
    }

    resolveCardPeriods($card, filters) {
        const periods = this.cardPeriodDetails.get($card) || [];
        const matchingPeriods = periods.filter((period) => {
            const matchesLocation = !filters.location || period.locationValue === filters.location;
            const matchesPeriod = !filters.period || period.periodValue === filters.period;

            return matchesLocation && matchesPeriod;
        });

        if (matchingPeriods.length) {
            return matchingPeriods;
        }

        if (filters.period) {
            const sameVacation = periods.filter((period) => period.periodValue === filters.period);
            if (sameVacation.length) {
                return sameVacation;
            }
        }

        if (filters.location) {
            const sameLocation = periods.filter((period) => period.locationValue === filters.location);
            if (sameLocation.length) {
                return sameLocation;
            }
        }

        return periods;
    }

    buildDateLabels($card, filters) {
        const $target = $card.querySelector('[data-camp-meta-date]');
        const overflowLabel = $target ? $target.dataset.campMetaDateOverflowLabel : '';
        const dates = this.uniqueValues(
            this.resolveCardPeriods($card, filters).map((period) => period.date),
        );

        if (dates.length > 2) {
            return [overflowLabel || 'Bekijk alle data'];
        }

        return dates;
    }

    setMetaDates($card, filters) {
        const $target = $card.querySelector('[data-camp-meta-date]');
        if (!$target) {
            return;
        }

        const dates = this.buildDateLabels($card, filters);
        $target.textContent = '';

        dates.forEach((date, index) => {
            const $date = document.createElement('span');
            $date.className = 'camp-card__meta-date';
            if (index > 0) {
                $date.classList.add('camp-card__meta-date--secondary');
                $date.textContent = `Ook: ${date}`;
            } else {
                $date.textContent = date;
            }

            $target.appendChild($date);
        });
    }

    setMetaText($card, selector, value) {
        const $target = $card.querySelector(selector);
        if ($target && value) {
            $target.textContent = value;
        }
    }

    setMetaExtra($card, selector, value) {
        const $target = $card.querySelector(selector);
        if (!$target) {
            return;
        }

        if (!value) {
            $target.textContent = '';
            $target.hidden = true;
            return;
        }

        $target.textContent = value;
        $target.hidden = false;
    }

    buildMetaExtra($card, field, activeValue, prefix) {
        const catalog = this.cardMetaCatalog.get($card);
        const values = catalog ? catalog[field] || [] : [];
        const remainder = values.filter((item) => item.normalized !== activeValue);

        if (!remainder.length) {
            return '';
        }

        return `${prefix} ${remainder.map((item) => item.label).join(', ')}`;
    }

    uniqueByNormalized(items) {
        const seen = new Set();

        return items.filter((item) => {
            if (!item.label || !item.normalized || seen.has(item.normalized)) {
                return false;
            }

            seen.add(item.normalized);
            return true;
        });
    }

    uniqueValues(values) {
        const seen = new Set();

        return values.filter((value) => {
            const normalized = this.normalize(value || '');
            if (!normalized || seen.has(normalized)) {
                return false;
            }

            seen.add(normalized);
            return true;
        });
    }

    normalize(value) {
        return value.toString().trim().toLowerCase();
    }

    values(value) {
        return value
            .toString()
            .split('|')
            .map((item) => this.normalize(item))
            .filter(Boolean);
    }
}
